import {
  AnalyticEventOptions,
  AnalyticEventTypes,
} from "@shared/lib/analytics/types/analytics-types";
import { enums, enumsTypes } from "@shared/constants";
import { VenueBasedEventSerializer } from "@app-contexts/dashboard/features/events/data/types/events";
import { showItemPasswordInput } from "@shared/utils/password/utils";
import { UserBasedDiscountTicketInfoSerializer } from "@shared/modules/discounts/types";

import {
  SeatingEventPropertyMap,
  SeatingItemPropertyMap,
} from "@shared/modules/seating/types/seating-property-types";
import { TicketBasketType } from "@shared/modules/payments/types";
import { TicketItemGroupType } from "@shared/modules/basket/types/common/ticket-item-group/ticket-item-group-type";

import { eventHasWaitlistItems, isSoldOutEvent } from "./event-utils";
import {
  NestedPublicVenueSerializer,
  PublicBaseEventSerializer,
  TicketTypeSerializer,
} from "../types/public";
import {
  isOnSaleTicketType,
  isTicketTypeSoldOut,
  isTicketTypeWaitlisted,
  isUpcomingSaleTicketType,
  voucherNotRequired,
} from "./ticket-type-utils";

/**
 * For events without passwords, returns true if ticket type is not in the past
 * For events with passwords, returns true if ticket type is not in the past and the ticket type id is in the display array
 * @param {object} inputTicket
 * @param {boolean} isPasswordProtected
 * @param {number[]} ticketsAuthorizedByPassword
 * @return {boolean}
 */
export const displayTicketType = (
  inputTicket: TicketTypeSerializer,
  isPasswordProtected: boolean,
  ticketsAuthorizedByPassword: number[],
  timezone: string
): boolean => {
  let returnValue = false;
  // Display only some ticket types, based on the event-level password
  if (
    isPasswordProtected &&
    ticketsAuthorizedByPassword &&
    ticketsAuthorizedByPassword.length > 0
  ) {
    if (ticketsAuthorizedByPassword.includes(inputTicket.id)) {
      returnValue = isUpcomingSaleTicketType(inputTicket, timezone);
    }
  }
  // Display all ticket types
  else {
    returnValue = isUpcomingSaleTicketType(inputTicket, timezone);
  }

  return returnValue;
};

/**
 * Returns if quantity buttons are allowed to be shown
 * If a payment plan, use the original ticket type as a parameter (to be safe)
 * as not all properties may be copied over from the original
 */
export const showTicketQuantityButtons = (
  type: TicketTypeSerializer | SeatingItemPropertyMap,
  inputEvent:
    | PublicBaseEventSerializer
    | VenueBasedEventSerializer
    | SeatingEventPropertyMap,
  userVouchers: UserBasedDiscountTicketInfoSerializer[],
  isDynamicHold: boolean
): boolean => {
  return (
    // Check if ticket is on sale
    isOnSaleTicketType(type) &&
    // AND it's not sold out OR waitlisted
    (!isTicketTypeSoldOut(type) || isTicketTypeWaitlisted(type)) &&
    // AND it's not a sold out event. If viewing a hold OR the event has waitlisted items,
    // allow adding to cart even if event is sold out
    (!isSoldOutEvent(inputEvent) ||
      isDynamicHold ||
      eventHasWaitlistItems(inputEvent)) &&
    // AND it's not a voucher-only ticket type
    voucherNotRequired(type, userVouchers)
  );
};

/**
 * Return number of the child event tickets currently in the user's cart
 * @param child childevent
 * @param basket ticketbaskettype
 * @return {Number}
 */
export const childTicketsInCart = (
  child: PublicBaseEventSerializer,
  basket: TicketBasketType
): number => {
  if (
    basket?.item_groups?.find((c: TicketItemGroupType) => c.event === child.id)
  ) {
    const qtyInCart = basket?.item_groups
      .filter((c: TicketItemGroupType) => c.event === child.id)
      .map((c: TicketItemGroupType) => c.quantity)
      .reduce((total: number, next: number) => total + next, 0);
    return qtyInCart;
  }
  return 0;
};

/**
 * Helper function used for tracking changes to the items in the cart.
 * @param debouncedAmountSelected New amount of items requested. Determines whether we fire 'add to cart' or 'remove from cart' events.
 * @param basket basket
 * @param itemId Item ID of the item
 * @param venue venue
 * @param analytics analytics object returned by our useAnalytics hook
 * @param spAddToCartCB callback function to trigger the UserInteractionLogger `add_to_cart` event
 * @param opts options passed to internal functions
 */
/**
 * @deprecated - use trackItemsInCart from useAnalyticsHelpers() instead
 */
export const trackItemsInCart = (
  debouncedAmountSelected: number,
  basket: TicketBasketType,
  itemInfo: {
    itemId: number;
    itemName: string;
    itemVariant: string;
    itemType: enumsTypes.ItemTypesMapIdType;
  },
  venue: NestedPublicVenueSerializer,
  analytics: Omit<AnalyticEventTypes, "init">,
  spAddToCartCB: () => void,
  opts?: AnalyticEventOptions
): void => {
  try {
    if (debouncedAmountSelected > 0 && basket.item_groups instanceof Array) {
      // Trigger `AddToCart` tracking tokens asynchronously
      const group = basket?.item_groups.filter(
        (ig: TicketItemGroupType) =>
          ig.type === itemInfo.itemId ||
          ig.product_attribute === itemInfo.itemId ||
          ig.membership_level === itemInfo.itemId
      );

      if (group.length > 0) {
        const newGroup = group[0];
        const formattedGroup = {
          ...newGroup,
          ...analytics.formatGtagTrackingData(
            itemInfo.itemId,
            itemInfo.itemName,
            itemInfo.itemVariant,
            itemInfo.itemType
          ),
        };
        analytics.addToCart(
          formattedGroup,
          debouncedAmountSelected,
          venue,
          opts
        );
        analytics.trackToken(
          enums.TRACKING_TOKEN_LOCATIONS.LOCATION_ADD_CART.id,
          venue.id,
          basket.item_groups?.map((item) => item.event),
          {
            fbEvent: "AddToCart",
            value: parseFloat(newGroup.price) * debouncedAmountSelected,
            currency: basket.currency,
            content_ids: newGroup.event || newGroup.membership_group,
            content_type: "product_group",
            name: newGroup.description,
          }
        );
        spAddToCartCB();
      }
    } else {
      analytics.removeFromCart();
    }
  } catch (err) {
    console.error(err); //eslint-disable-line
  }
};

export const showTicketButtons = (
  tt: TicketTypeSerializer | SeatingItemPropertyMap,
  eventData:
    | PublicBaseEventSerializer
    | VenueBasedEventSerializer
    | SeatingEventPropertyMap,
  userVouchers: UserBasedDiscountTicketInfoSerializer[],
  ticketAuthArray: Array<{ id: number; authorized: boolean }>,
  updateTicketAuthArray: (
    input: Array<{ id: number; authorized: boolean }>
  ) => void,
  basketVoucherCode: string,
  isDynamicHold = false
): boolean =>
  showTicketQuantityButtons(tt, eventData, userVouchers, isDynamicHold) &&
  !showItemPasswordInput(tt, ticketAuthArray, updateTicketAuthArray) &&
  !(tt.voucher_purchases_only && basketVoucherCode === "");
